// MessageAlert.tsx
import React, { FunctionComponent, useEffect, useState, useRef } from "react";
import styled, { css } from "styled-components";
import Icon from "../global/Icon";
import Colors from "../global/Colors";
import Fonts from "../global/Fonts";
import Button from './Button';
import _ from 'lodash';
import { useMediaQuery } from '@mui/material';

const getBGColor = (colorName, colorMode) => {
    const colorMapping = {
        red: Colors.redPale,
        yellow: Colors.yellowPale,
        green: Colors.greenPale,
        white: Colors.white,
        black: Colors.grey9
    };
    if (colorMode === 'dark') {
        const darkModeColorMapping = {
            red: Colors.redDark, // Assuming you have a dark red
            yellow: Colors.yellowDark, // Assuming you have a dark yellow
            green: Colors.greenDark, // Assuming you have a dark green
            white: Colors.grey8, // Dark mode background for 'white' alert
            black: Colors.grey2, // Dark mode background for 'black' alert
        };
        return darkModeColorMapping[colorName] || Colors.grey2;
    }
    return colorMapping[colorName] || Colors.greenPale; // default
};

const getAccentColor = (colorName, colorMode) => {
    const accentColorMapping = {
        red: Colors.red,
        yellow: Colors.yellow,
        green: Colors.green,
        white: Colors.transparent,
        black: Colors.transparent,
    };
    if (colorMode === 'dark') {
        const darkAccentColorMapping = {
            red: Colors.redLight, // Example: a lighter red for dark mode accent
            yellow: Colors.yellowLight, // Example: a lighter yellow for dark mode accent
            green: Colors.greenLight, // Example: a lighter green for dark mode accent
            white: Colors.transparent,
            black: Colors.transparent,
        };
        return darkAccentColorMapping[colorName] || Colors.greenLight;
    }
    return accentColorMapping[colorName] || Colors.green; // default
};

const getIconType = (colorName) => {
    const iconMapping = {
        red: 'exclamation',
        yellow: 'exclamation',
        green: 'info',
        white: 'loading',
        black: 'loading',
    };
    return iconMapping[colorName] || 'info'; // default
};

const getTitleTextColor = (colorName, colorMode) => {
    if (colorMode === 'dark') {
        return Colors.white; // All titles white in dark mode
    }
    return colorName === 'black' ? Colors.white : Colors.grayMed;
};

const getBodyTextColor = (colorName, colorMode) => {
    if (colorMode === 'dark') {
        return Colors.grey2; // Body text slightly darker white in dark mode
    }
    return colorName === 'black' ? Colors.white : Colors.grayMed;
};

const MessageBackground = styled.div<{ $size: 'small' | 'large'; $isMobile: boolean; $reflowButtons: boolean; $colorMode: 'light' | 'dark'; $hasChildren?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: ${({ $reflowButtons }) => ($reflowButtons ? 'column' : 'row')};
    align-items: ${({ $reflowButtons }) => ($reflowButtons ? 'center' : 'flex-start')};
    justify-content: center;
    max-height: calc(100svh - 10px);
    overflow-y: auto;

    background-color: ${({ color, $colorMode }) => getBGColor(color, $colorMode)};
    min-width: ${({ $isMobile }) => ($isMobile ? '98vw' : '300px')};
    width: fit-content;
    max-width: ${({ $isMobile }) => ($isMobile ? '100%' : 'calc(100% - 20px)')};
    border-radius: 8px;
    padding: ${({ $size, $isMobile }) =>
        ($size === 'large' && $isMobile ? '8px 12px 8px 12px' : $size === 'large' ? '12px 14px 12px 14px' :
        $size === 'small' && $isMobile ? '7px 8px 7px 8px' : '8px 10px 8px 10px')};
    margin: auto;
    box-shadow: -2px 6px 6px rgba(0,0,0,0.1);
    z-index: 100;
    column-gap: ${({ $isMobile }) => ($isMobile ? '10px' : '12px')};
    row-gap: 4px;
    text-align: left;
`;

const IconContainer = styled.div<{ $size: 'small' | 'large'; $colorMode: 'light' | 'dark' }>`
    border-radius: 50%;
    background-color: ${({ color, $colorMode }) => getAccentColor(color, $colorMode)};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${({ $size }) => ($size === 'large' ? '10px' : '8px')};
    align-self: flex-start;
`;

const TextContainer = styled.div<{ $size: 'small' | 'large' }>`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: ${({ $size }) => ($size === 'large' ? '4px' : '3px')};
    padding: 0;
    margin: 0 auto;
`;

const TitleText = styled.h6<{ $colorMode: 'light' | 'dark' }>`
    font-weight: ${Fonts.lexendSemiBold.fontWeight};
    font-family: ${Fonts.lexendSemiBold.fontFamily};
    font-size: 15px;
    line-height: 1.2em;
    color: ${({ color, $colorMode }) => getTitleTextColor(color, $colorMode)};
    padding: 0;
    margin: 0;
`;

const BodyText = styled.p<{ $colorMode: 'light' | 'dark' }>`
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-size: 14px;
    line-height: 1.1em;
    color: ${({ color, $colorMode }) => getBodyTextColor(color, $colorMode)};
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    width: 100%;
    padding: 0;
    margin: 0;
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    gap: 5px;
    margin-left: auto;
    padding-top: 4px;
`;

const MobileButtonGroup = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 5px;
    width: 100%;
`;

const MainContent = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    gap: inherit;
    width: 100%;
`;

const ContentAndButtonsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 100%;
    gap: inherit;
`;

const ChildrenContainer = styled.div`
    width: 100%;
    max-width: 400px;
`;

export type MessageAlertType = {
    titleText?: string;
    bodyText?: string | string[];
    children?: React.ReactNode;
    color: string;
    onClose?: () => void;
    includeClose?: boolean;
    size?: 'small' | 'large';
    primaryButton?: {
        text?: string;
        icon?: string;
        onClick: () => void;
        disabled?: boolean;
    };
    secondaryButton?: {
        text?: string;
        icon?: string;
        onClick: () => void;
        disabled?: boolean;
    };
    colorMode?: 'light' | 'dark';
};

const MessageAlert: FunctionComponent<MessageAlertType> = ({
    titleText,
    bodyText,
    color,
    onClose,
    includeClose = false,
    size = 'large',
    primaryButton,
    secondaryButton,
    colorMode = 'light',
    children,
 }) => {
    const isMobile = useMediaQuery('(max-width: 400px)');
    const reflowButtons = useMediaQuery('(max-width: 768px)');
    const modalRef = useRef<HTMLDivElement>(null);

    return (
        <MessageBackground color={color} ref={modalRef} $size={size} $isMobile={isMobile} $reflowButtons={reflowButtons} $colorMode={colorMode} $hasChildren={!!children}>
            <MainContent>
                {color !== 'white' && color !== 'black' && (
                    <IconContainer color={color} $size={size} $colorMode={colorMode}>
                        <Icon icon={getIconType(color)} size={28} color={Colors.white} onClick={() => {}} />
                    </IconContainer>
                )}
                <ContentAndButtonsWrapper>
                    <TextContainer id="text-container" $size={size}>
                        {titleText && <TitleText color={color} $colorMode={colorMode}>{titleText}</TitleText>}
                        {bodyText && Array.isArray(bodyText) ? (
                            bodyText.map((text, index) => (
                                <BodyText key={index} color={color} $colorMode={colorMode}>{text}</BodyText>
                            ))
                        ) : (
                            bodyText && <BodyText color={color} $colorMode={colorMode}>{bodyText}</BodyText>
                        )}
                        {children && <ChildrenContainer>
                            {children}
                        </ChildrenContainer>}
                    </TextContainer>
                    <ButtonGroup>
                        {!reflowButtons && secondaryButton && (
                            <Button
                                size={isMobile || size === 'small' ? 'tiny' : 'small'}
                                text={secondaryButton.text}
                                icon={secondaryButton.icon}
                                color={colorMode === 'dark' ? 'black' : (color === 'black' ? 'black' : 'white')}
                                onClick={secondaryButton.onClick}
                                disabled={secondaryButton.disabled}
                            />
                        )}
                        {!reflowButtons && primaryButton && (
                            <Button
                                size={isMobile || size === 'small' ? 'tiny' :'small'}
                                text={primaryButton.text}
                                icon={primaryButton.icon}
                                color={colorMode === 'dark' ? 'white' : (color === 'black' ? 'white' : 'black')}
                                onClick={primaryButton.onClick}
                                disabled={primaryButton.disabled}
                            />
                        )}
                        {includeClose !== false && (
                            <Button
                                size={isMobile || size === 'small' ? 'tiny' :'small'}
                                icon={'delete'}
                                color={colorMode === 'dark' ? 'ghost white' : (color === 'black' ? 'black' : 'ghost')}
                                onClick={onClose}
                            />
                        )}
                    </ButtonGroup>
                </ContentAndButtonsWrapper>
            </MainContent>
            {reflowButtons && (primaryButton || secondaryButton) && (
                <MobileButtonGroup>
                    {secondaryButton && (
                        <Button
                            size={'tiny'}
                            text={secondaryButton.text}
                            icon={secondaryButton.icon}
                            color={colorMode === 'dark' ? 'black' : (color === 'black' ? 'black' : 'white')}
                            onClick={secondaryButton.onClick}
                            disabled={secondaryButton.disabled}
                        />
                    )}
                    {primaryButton && (
                        <Button
                            size={'tiny'}
                            text={primaryButton.text}
                            icon={primaryButton.icon}
                            color={colorMode === 'dark' ? 'white' : (color === 'black' ? 'white' : 'black')}
                            onClick={primaryButton.onClick}
                            disabled={primaryButton.disabled}
                        />
                    )}
                </MobileButtonGroup>
            )}
        </MessageBackground>
    );
}

export default MessageAlert;